import { Delegate } from './delegate.js';

if (!customElements.get('product-collapsible-tabs')) {
  const TRANSITION_FALLBACK = 1000;
  const RESIZE_DEBOUNCE = 150;
  /* Collapsing scrolls the chips back into view on mobile only -- on desktop
     they are pinned already, so moving the page would just be disorienting. */
  const SCROLL_BACK_QUERY = '(max-width: 767.98px)';

  /* Expanding usually pushes the panel's bottom edge past the fold within a
     frame, so a long distance is animated slightly slower and eased in -- that
     keeps the part of the motion that is still on screen legible. Collapsing
     brings the edge back into view, so it keeps the ease-out curve. */
  const EASE_EXPAND = 'cubic-bezier(0.65, 0, 0.35, 1)';
  const EASE_COLLAPSE = 'cubic-bezier(0.33, 1, 0.68, 1)';
  const durationFor = (distance) => Math.min(650, Math.max(350, 250 + distance * 0.12));

  class ProductCollapsibleTabs extends HTMLElement {
    #delegate = null;
    #tabs = [];
    #panels = [];
    #toggle = null;
    #header = null;
    #activeIndex = 0;
    #expanded = false;
    #resizeTimer = 0;

    connectedCallback() {
      this.#tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.#panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.#toggle = this.querySelector('[data-action="toggle-panel"]');
      this.#header = this.querySelector('.product-collapsible-tabs__header');

      const initialIndex = Number(this.dataset.initialTab) || 0;
      this.#activate(initialIndex);

      this.#delegate = new Delegate(this);
      this.#delegate.on('click', '[data-action="select-tab"]', this.#onTabClick);
      this.#delegate.on('click', '[data-action="toggle-panel"]', this.#onToggleClick);

      window.addEventListener('resize', this.#onResize);
    }

    disconnectedCallback() {
      this.#delegate?.destroy();
      this.#delegate = null;
      window.removeEventListener('resize', this.#onResize);
      clearTimeout(this.#resizeTimer);
      this.#tabs = [];
      this.#panels = [];
      this.#toggle = null;
      this.#header = null;
    }

    get #activePanel() {
      return this.#panels[this.#activeIndex] || null;
    }

    get #motionReduced() {
      return (
        window.FoxTheme?.config?.motionReduced ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      );
    }

    /**
     * Handles delegated click on a tab button.
     * @param {MouseEvent} event
     * @param {HTMLElement} tab
     */
    #onTabClick = (event, tab) => {
      event.preventDefault();

      const index = Number(tab?.dataset?.index);
      if (!Number.isNaN(index)) {
        this.#activate(index);
      }
    };

    /**
     * Handles delegated click on the read more/less button.
     * @param {MouseEvent} event
     */
    #onToggleClick = (event) => {
      event.preventDefault();
      this.#expanded ? this.#collapse() : this.#expand();
    };

    #onResize = () => {
      clearTimeout(this.#resizeTimer);
      this.#resizeTimer = setTimeout(() => {
        // Re-measure only while collapsed; reflowing an open panel shut would
        // yank the reader's position out from under them.
        if (!this.#expanded) this.#resetCollapse();
      }, RESIZE_DEBOUNCE);
    };

    /**
     * Activates a tab/panel by index.
     * @param {number} index
     */
    #activate(index) {
      if (
        index < 0 ||
        index >= this.#tabs.length ||
        index >= this.#panels.length
      )
        return;

      this.#tabs.forEach((tab, i) => {
        const isActive = i === index;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.classList.toggle('btn--active', isActive);
      });

      this.#panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });

      this.#activeIndex = index;
      this.#resetCollapse();
    }

    /**
     * Puts the active panel back into its collapsed state and shows the toggle
     * only when the content actually overflows the clamp.
     */
    #resetCollapse() {
      const panel = this.#activePanel;
      if (!panel || !this.#toggle) return;

      this.#expanded = false;
      panel.style.maxHeight = '';
      panel.classList.remove('is-animating');
      this.#setTiming(panel, null);

      // Clamp first, then keep it only if the content really is taller: otherwise
      // the fade would sit over text that is already fully visible.
      panel.classList.add('is-collapsed');
      const overflows = panel.scrollHeight > panel.clientHeight + 1;
      panel.classList.toggle('is-collapsed', overflows);

      this.#setToggleState(false);
      this.#toggle.setAttribute('aria-controls', panel.id);
      this.#toggle.hidden = !overflows;
    }

    /**
     * Measures the panel in both states without leaving a visual trace.
     * @param {HTMLElement} panel
     * @returns {{collapsed: number, full: number}} heights in pixels
     */
    #measure(panel) {
      const inlineMaxHeight = panel.style.maxHeight;
      const wasCollapsed = panel.classList.contains('is-collapsed');

      panel.style.maxHeight = '';
      panel.classList.add('is-collapsed');
      const collapsed = panel.clientHeight;
      const full = panel.scrollHeight;

      if (!wasCollapsed) panel.classList.remove('is-collapsed');
      panel.style.maxHeight = inlineMaxHeight;

      return { collapsed, full };
    }

    #expand() {
      const panel = this.#activePanel;
      if (!panel) return;

      const { collapsed, full } = this.#measure(panel);

      this.#expanded = true;
      this.#setToggleState(true);

      if (this.#motionReduced) {
        panel.classList.remove('is-collapsed');
        panel.style.maxHeight = '';
        return;
      }

      panel.classList.add('is-animating');
      this.#setTiming(panel, { distance: full - collapsed, easing: EASE_EXPAND, expanding: true });
      this.#pinStart(panel, collapsed);
      panel.classList.remove('is-collapsed');
      panel.style.maxHeight = `${full}px`;

      this.#afterTransition(panel, () => {
        panel.classList.remove('is-animating');
        // Drop the clamp so the panel can reflow freely once it is open.
        if (this.#expanded) panel.style.maxHeight = '';
      });
    }

    #collapse() {
      const panel = this.#activePanel;
      if (!panel) return;

      const { collapsed, full } = this.#measure(panel);

      this.#expanded = false;
      this.#setToggleState(false);
      this.#scrollToHeader();

      if (this.#motionReduced) {
        panel.classList.add('is-collapsed');
        panel.style.maxHeight = '';
        return;
      }

      panel.classList.add('is-animating');
      this.#setTiming(panel, { distance: full - collapsed, easing: EASE_COLLAPSE, expanding: false });
      this.#pinStart(panel, full);
      panel.classList.add('is-collapsed');
      panel.style.maxHeight = `${collapsed}px`;

      this.#afterTransition(panel, () => {
        panel.classList.remove('is-animating');
        if (!this.#expanded) panel.style.maxHeight = '';
      });
    }

    /**
     * Commits a starting max-height with transitions suppressed, so what follows
     * always animates from an explicit, interpolable value. #measure() reads
     * clientHeight while clamped, which leaves the collapsed height as the last
     * committed value -- without this the browser animates from the wrong end,
     * or (when the two happen to coincide) not at all.
     * @param {HTMLElement} panel
     * @param {number} height
     */
    #pinStart(panel, height) {
      panel.style.transition = 'none';
      panel.style.maxHeight = `${height}px`;
      panel.offsetHeight; // Commit it while transitions are off.
      panel.style.transition = '';
    }

    /**
     * Applies (or clears) the per-animation duration and easing.
     * @param {HTMLElement} panel
     * @param {{distance: number, easing: string}|null} timing
     */
    #setTiming(panel, timing) {
      if (!timing) {
        panel.style.removeProperty('--collapsible-tabs-duration');
        panel.style.removeProperty('--collapsible-tabs-easing');
        panel.style.removeProperty('--collapsible-tabs-fade-duration');
        panel.style.removeProperty('--collapsible-tabs-fade-delay');
        return;
      }

      const duration = durationFor(Math.abs(timing.distance));
      // Expanding: hold the gradient over the moving clip edge, then fade it out
      // just past the end. Collapsing: fade it straight back in and keep it.
      const fadeDuration = timing.expanding ? duration * 0.5 : Math.min(200, duration * 0.4);
      const fadeDelay = timing.expanding ? duration * 0.55 : 0;

      panel.style.setProperty('--collapsible-tabs-duration', `${Math.round(duration)}ms`);
      panel.style.setProperty('--collapsible-tabs-easing', timing.easing);
      panel.style.setProperty('--collapsible-tabs-fade-duration', `${Math.round(fadeDuration)}ms`);
      panel.style.setProperty('--collapsible-tabs-fade-delay', `${Math.round(fadeDelay)}ms`);
    }

    /**
     * @param {boolean} expanded
     */
    #setToggleState(expanded) {
      this.#toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      this.#toggle.classList.toggle('is-expanded', expanded);
    }

    /**
     * Brings the chips back into view when the panel is collapsed again.
     */
    #scrollToHeader() {
      if (!this.#header) return;
      if (!window.matchMedia(SCROLL_BACK_QUERY).matches) return;

      const headerHeight =
        parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--header-height')
        ) || 0;
      // Measure the block wrapper rather than the host or the header: the header
      // is sticky (so its rect reads as the pinned position) and the host is a
      // custom element, whose box is unreliable if it is ever left inline.
      const anchor = this.closest('.product__block--collapsible_tabs') || this;
      const top = Math.max(0, anchor.getBoundingClientRect().top + window.scrollY - headerHeight);

      if (window.scrollY <= top + 1) return;

      window.scrollTo({ top, behavior: this.#motionReduced ? 'auto' : 'smooth' });
    }

    /**
     * Runs a callback once the max-height transition ends, with a timeout guard.
     * @param {HTMLElement} element
     * @param {Function} done
     */
    #afterTransition(element, done) {
      let settled = false;

      const finish = () => {
        if (settled) return;
        settled = true;
        element.removeEventListener('transitionend', onTransitionEnd);
        clearTimeout(timer);
        done();
      };

      const onTransitionEnd = (event) => {
        if (event.target === element && event.propertyName === 'max-height') finish();
      };

      element.addEventListener('transitionend', onTransitionEnd);
      const timer = setTimeout(finish, TRANSITION_FALLBACK);
    }
  }

  customElements.define('product-collapsible-tabs', ProductCollapsibleTabs);
}
